/**
 * Cognito Hosted UI + Microsoft 365 federated sign-in for the Office task pane.
 *
 * Flow (see docs/2026-06-10-sso-federation-final.md §5):
 *
 *   task pane → displayDialogAsync(auth/start.html?to=<authorize URL>)
 *     → start.html (same-origin) redirects to Cognito /oauth2/authorize
 *       with identity_provider=M365 and PKCE
 *     → Cognito → Entra ID (silent or interactive)
 *     → Cognito → /auth/callback.html?code=…&state=…
 *     → callback.html calls Office.context.ui.messageParent(payload)
 *     → task pane validates state, exchanges code (+ PKCE verifier) for tokens
 *       at /oauth2/token, writes sessionStorage["agentcore.sso.tokens"].
 *
 * Amplify v6 stays configured so other code can still call fetchAuthSession
 * semantics, but tokens live in sessionStorage — we never push the federated
 * session into Amplify's store (it has no public API to do so). The public
 * surface (`getCurrentAuthUser`, `getIdToken`, `signOut`, `AuthUser`) keeps
 * its existing shape so `App.tsx` and `api/client.ts` are unaffected.
 */

import { Amplify } from 'aws-amplify';

const USER_POOL_ID = import.meta.env.VITE_USER_POOL_ID || '';
const USER_POOL_CLIENT_ID = import.meta.env.VITE_USER_POOL_CLIENT_ID || '';
const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN || '';
const IDENTITY_PROVIDER = import.meta.env.VITE_COGNITO_IDENTITY_PROVIDER || 'M365';
const REDIRECT_SIGN_IN = import.meta.env.VITE_OAUTH_REDIRECT_SIGN_IN || '';
const REDIRECT_SIGN_OUT = import.meta.env.VITE_OAUTH_REDIRECT_SIGN_OUT || '';
const OAUTH_SCOPES = (import.meta.env.VITE_OAUTH_SCOPES || 'openid email profile')
  .split(/\s+/)
  .filter(Boolean);

if (!USER_POOL_ID || !USER_POOL_CLIENT_ID || !COGNITO_DOMAIN || !REDIRECT_SIGN_IN) {
  // eslint-disable-next-line no-console
  console.error(
    'Office add-in env missing one of: VITE_USER_POOL_ID, VITE_USER_POOL_CLIENT_ID, ' +
      'VITE_COGNITO_DOMAIN, VITE_OAUTH_REDIRECT_SIGN_IN.',
  );
}

const TOKENS_KEY = 'agentcore.sso.tokens';
const PKCE_KEY = 'agentcore.sso.pkce';
/** `dialog` = Office displayDialogAsync; `browser` = full-page redirect from index.html. */
const SSO_MODE_KEY = 'agentcore.sso.mode';
/** Authorize → token-exchange round-trip must complete inside this window. */
const PKCE_TTL_MS = 10 * 60 * 1000;
/** Refresh proactively this many seconds before `expires_in` to avoid races. */
const REFRESH_SKEW_SECONDS = 60;

let configured = false;
function ensureConfigured() {
  if (configured) return;
  // Amplify is kept configured for any future call site that wants
  // `fetchAuthSession`-style semantics. The add-in does NOT push tokens here.
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: USER_POOL_ID,
        userPoolClientId: USER_POOL_CLIENT_ID,
        signUpVerificationMethod: 'code',
      },
    },
  });
  configured = true;
}

export interface AuthUser {
  username: string;
  email?: string;
}

interface StoredTokens {
  id_token: string;
  access_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
  /** Epoch ms at which the token set was minted (or refreshed). */
  obtainedAt: number;
}

interface PkceRecord {
  code_verifier: string;
  state: string;
  redirect_uri: string;
  createdAt: number;
}

// ── Storage helpers ───────────────────────────────────────────────────────

function readTokens(): StoredTokens | null {
  try {
    const raw = window.sessionStorage.getItem(TOKENS_KEY);
    return raw ? (JSON.parse(raw) as StoredTokens) : null;
  } catch {
    return null;
  }
}

function writeTokens(t: StoredTokens) {
  window.sessionStorage.setItem(TOKENS_KEY, JSON.stringify(t));
}

function clearTokens() {
  try {
    window.sessionStorage.removeItem(TOKENS_KEY);
  } catch {
    /* sessionStorage may be unavailable; nothing useful to do */
  }
}

function readPkce(): PkceRecord | null {
  try {
    const raw = window.sessionStorage.getItem(PKCE_KEY);
    return raw ? (JSON.parse(raw) as PkceRecord) : null;
  } catch {
    return null;
  }
}

function writePkce(p: PkceRecord) {
  window.sessionStorage.setItem(PKCE_KEY, JSON.stringify(p));
}

function clearPkce() {
  try {
    window.sessionStorage.removeItem(PKCE_KEY);
  } catch {
    /* ignore */
  }
}

// ── PKCE helpers ──────────────────────────────────────────────────────────

function base64url(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return window.btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomBytes(n: number): Uint8Array {
  const a = new Uint8Array(n);
  window.crypto.getRandomValues(a);
  return a;
}

async function sha256(input: string): Promise<Uint8Array> {
  const data = new TextEncoder().encode(input);
  const buf = await window.crypto.subtle.digest('SHA-256', data);
  return new Uint8Array(buf);
}

function makeVerifier(): string {
  // 32 random bytes encode to a 43-char base64url string — within the
  // RFC 7636 43–128 range.
  return base64url(randomBytes(32));
}

async function makeChallenge(verifier: string): Promise<string> {
  return base64url(await sha256(verifier));
}

function makeState(): string {
  return base64url(randomBytes(16));
}

// ── Authorize-URL / start-URL construction ────────────────────────────────

function buildAuthorizeUrl(challenge: string, state: string): string {
  // URLSearchParams handles spaces in scope correctly (`+` per the spec).
  const params = new URLSearchParams();
  params.set('client_id', USER_POOL_CLIENT_ID);
  params.set('response_type', 'code');
  params.set('scope', OAUTH_SCOPES.join(' '));
  params.set('redirect_uri', REDIRECT_SIGN_IN);
  params.set('identity_provider', IDENTITY_PROVIDER);
  params.set('code_challenge', challenge);
  params.set('code_challenge_method', 'S256');
  params.set('state', state);
  return `https://${COGNITO_DOMAIN}/oauth2/authorize?${params.toString()}`;
}

function buildStartUrl(authorizeUrl: string): string {
  // displayDialogAsync only accepts a same-origin initial URL. start.html
  // sits at the add-in's origin and does the cross-origin redirect itself.
  const origin = new URL(REDIRECT_SIGN_IN).origin;
  return `${origin}/auth/start.html?to=${encodeURIComponent(authorizeUrl)}`;
}

function getAppEntryUrl(): string {
  if (REDIRECT_SIGN_OUT) return REDIRECT_SIGN_OUT;
  return new URL('/index.html', new URL(REDIRECT_SIGN_IN).origin).href;
}

function canUseOfficeDialog(): boolean {
  return typeof Office !== 'undefined' && !!Office?.context?.ui?.displayDialogAsync;
}

function clearSsoMode() {
  try {
    window.sessionStorage.removeItem(SSO_MODE_KEY);
  } catch {
    /* ignore */
  }
}

async function createPkceRecord(): Promise<{ record: PkceRecord; authorizeUrl: string }> {
  const codeVerifier = makeVerifier();
  const codeChallenge = await makeChallenge(codeVerifier);
  const state = makeState();
  const record: PkceRecord = {
    code_verifier: codeVerifier,
    state,
    redirect_uri: REDIRECT_SIGN_IN,
    createdAt: Date.now(),
  };
  writePkce(record);
  return { record, authorizeUrl: buildAuthorizeUrl(codeChallenge, state) };
}

// ── Sign-in flows ─────────────────────────────────────────────────────────

interface DialogResultMessage {
  type?: string;
  code?: string | null;
  state?: string | null;
  error?: string | null;
}

async function finishAuthorizationFromCallback(payload: DialogResultMessage): Promise<AuthUser> {
  const fresh = readPkce();
  if (
    !fresh ||
    fresh.state !== payload.state ||
    Date.now() - fresh.createdAt > PKCE_TTL_MS
  ) {
    clearPkce();
    throw new Error('Sign-in state mismatch — please try again.');
  }
  if (payload.error) {
    clearPkce();
    throw new Error(`Microsoft sign-in returned an error: ${payload.error}`);
  }
  if (!payload.code) {
    clearPkce();
    throw new Error('Microsoft sign-in did not return an authorization code.');
  }

  let tokens: StoredTokens;
  try {
    tokens = await exchangeCodeForTokens(payload.code, fresh);
  } finally {
    clearPkce();
  }
  writeTokens(tokens);

  const user = userFromIdToken(tokens.id_token);
  if (!user) throw new Error('Token exchange succeeded but the ID token is unreadable.');
  return user;
}

/**
 * Complete a browser redirect sign-in on `/auth/callback.html` after Cognito
 * returns. Redirects back to `/index.html` with tokens in sessionStorage.
 */
export async function completeBrowserOAuthCallback(): Promise<void> {
  ensureConfigured();
  const params = new URLSearchParams(window.location.search);
  const payload: DialogResultMessage = {
    type: 'sso-result',
    code: params.get('code'),
    state: params.get('state'),
    error: params.get('error') || params.get('error_description'),
  };

  try {
    await finishAuthorizationFromCallback(payload);
  } finally {
    clearSsoMode();
  }

  window.location.replace(getAppEntryUrl());
}

/**
 * Sign in with Microsoft 365 via Cognito federated OIDC + PKCE.
 *
 * Inside Office hosts, opens `displayDialogAsync` (same-origin start page →
 * Cognito → callback → messageParent). In a normal browser tab (e.g.
 * https://addin.agentcore.megazonehk.com/index.html), performs a full-page
 * redirect to Cognito and completes on `/auth/callback.html`.
 */
export async function signInWithMicrosoft(): Promise<AuthUser> {
  ensureConfigured();
  if (!COGNITO_DOMAIN || !REDIRECT_SIGN_IN) {
    throw new Error('Cognito Hosted UI is not configured for this build.');
  }

  const { authorizeUrl } = await createPkceRecord();

  if (canUseOfficeDialog()) {
    clearSsoMode();
    const startUrl = buildStartUrl(authorizeUrl);

    let payload: DialogResultMessage;
    try {
      payload = await openSsoDialog(startUrl);
    } catch (err) {
      clearPkce();
      throw err;
    }

    return finishAuthorizationFromCallback(payload);
  }

  // Browser / non-Office preview: same PKCE record, full-page redirect.
  window.sessionStorage.setItem(SSO_MODE_KEY, 'browser');
  window.location.assign(authorizeUrl);
  return new Promise<AuthUser>(() => {
    /* page navigates away */
  });
}

function openSsoDialog(url: string): Promise<DialogResultMessage> {
  return new Promise((resolve, reject) => {
    Office.context.ui.displayDialogAsync(
      url,
      { height: 60, width: 40, displayInIframe: false },
      (asyncResult) => {
        if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
          reject(
            new Error(
              `Failed to open sign-in dialog: ${asyncResult.error?.message ?? 'unknown error'}`,
            ),
          );
          return;
        }
        const dialog = asyncResult.value;
        let settled = false;

        dialog.addEventHandler(
          Office.EventType.DialogMessageReceived,
          (arg) => {
            if (!('message' in arg)) return;
            if (settled) return;
            settled = true;
            let parsed: DialogResultMessage;
            try {
              parsed = JSON.parse(arg.message) as DialogResultMessage;
            } catch {
              dialog.close();
              reject(new Error('Sign-in dialog returned an unreadable message.'));
              return;
            }
            // Close the dialog before resolving so callers update UI on a
            // clean slate.
            dialog.close();
            resolve(parsed);
          },
        );

        dialog.addEventHandler(
          Office.EventType.DialogEventReceived,
          (arg) => {
            if (!('error' in arg)) return;
            if (settled) return;
            settled = true;
            // 12006 = dialog closed by the user.
            if (arg.error === 12006) {
              reject(new Error('Sign-in was cancelled.'));
            } else {
              reject(new Error(`Sign-in dialog error (${arg.error}).`));
            }
          },
        );
      },
    );
  });
}

// ── Token exchange / refresh ──────────────────────────────────────────────

async function exchangeCodeForTokens(code: string, pkce: PkceRecord): Promise<StoredTokens> {
  const body = new URLSearchParams();
  body.set('grant_type', 'authorization_code');
  body.set('client_id', USER_POOL_CLIENT_ID);
  body.set('code', code);
  body.set('redirect_uri', pkce.redirect_uri);
  body.set('code_verifier', pkce.code_verifier);

  const res = await fetch(`https://${COGNITO_DOMAIN}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Token exchange failed (${res.status}): ${text || 'no response body'}`);
  }
  const data = (await res.json()) as {
    id_token: string;
    access_token: string;
    refresh_token?: string;
    token_type: string;
    expires_in: number;
  };
  return { ...data, obtainedAt: Date.now() };
}

/** Single in-flight refresh shared across concurrent getIdToken() callers. */
let inflightRefresh: Promise<StoredTokens | null> | null = null;

async function refreshIdTokenIfNeeded(): Promise<StoredTokens | null> {
  const current = readTokens();
  if (!current) return null;

  const ageSeconds = (Date.now() - current.obtainedAt) / 1000;
  if (ageSeconds < Math.max(0, current.expires_in - REFRESH_SKEW_SECONDS)) {
    return current;
  }
  if (!current.refresh_token) {
    clearTokens();
    return null;
  }

  if (!inflightRefresh) {
    inflightRefresh = (async () => {
      try {
        const body = new URLSearchParams();
        body.set('grant_type', 'refresh_token');
        body.set('client_id', USER_POOL_CLIENT_ID);
        body.set('refresh_token', current.refresh_token!);

        const res = await fetch(`https://${COGNITO_DOMAIN}/oauth2/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: body.toString(),
        });
        if (!res.ok) {
          clearTokens();
          return null;
        }
        const next = (await res.json()) as {
          id_token: string;
          access_token: string;
          refresh_token?: string;
          token_type: string;
          expires_in: number;
        };
        // Cognito refresh-token responses usually omit `refresh_token`; keep
        // the existing one when that happens.
        const merged: StoredTokens = {
          ...next,
          refresh_token: next.refresh_token || current.refresh_token,
          obtainedAt: Date.now(),
        };
        writeTokens(merged);
        return merged;
      } catch {
        clearTokens();
        return null;
      } finally {
        inflightRefresh = null;
      }
    })();
  }
  return inflightRefresh;
}

// ── Public API consumed by App.tsx / api/client.ts ───────────────────────

export async function getCurrentAuthUser(): Promise<AuthUser | null> {
  ensureConfigured();
  const t = await refreshIdTokenIfNeeded();
  if (!t) return null;
  return userFromIdToken(t.id_token);
}

export async function getIdToken(): Promise<string | null> {
  ensureConfigured();
  const t = await refreshIdTokenIfNeeded();
  return t ? t.id_token : null;
}

export async function getAccessToken(): Promise<string | null> {
  ensureConfigured();
  const t = await refreshIdTokenIfNeeded();
  return t ? t.access_token : null;
}

export async function signOut(): Promise<void> {
  ensureConfigured();
  const t = readTokens();
  clearTokens();
  clearPkce();
  clearSsoMode();
  if (t?.refresh_token) {
    // Best-effort revoke; the user is logged out from the add-in's perspective
    // regardless of the result.
    try {
      const body = new URLSearchParams();
      body.set('token', t.refresh_token);
      body.set('client_id', USER_POOL_CLIENT_ID);
      await fetch(`https://${COGNITO_DOMAIN}/oauth2/revoke`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
    } catch {
      /* best-effort */
    }
  }
}

/** Exported for callers that need the configured sign-out redirect target. */
export function getSignOutRedirect(): string {
  return REDIRECT_SIGN_OUT;
}

// ── Helpers ───────────────────────────────────────────────────────────────

function userFromIdToken(idToken: string): AuthUser | null {
  const payload = decodeJwtPayload(idToken);
  if (!payload) return null;
  const email = typeof payload.email === 'string' ? payload.email : undefined;
  const username =
    (typeof payload['cognito:username'] === 'string' && payload['cognito:username']) ||
    (typeof payload.sub === 'string' && payload.sub) ||
    email ||
    'user';
  return { username, email };
}

function decodeJwtPayload(jwt: string): Record<string, unknown> | null {
  const parts = jwt.split('.');
  if (parts.length < 2) return null;
  try {
    // base64url → base64 → utf-8
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = '='.repeat((4 - (b64.length % 4)) % 4);
    const json = window.atob(b64 + pad);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}
